import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n'; // using useI18n instead of useTranslation to avoid circular dependency

export default () => {
  const { t } = useI18n();

  const handleDownloadError = (imageUrl: string, error: string | Error | Response): void => {
    const isCorsError = hasCorsOrCrossOriginError(imageUrl, error);

    // Open image in new tab as fallback
    const anchor: HTMLAnchorElement = document.createElement('a');
    anchor.href = imageUrl;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.dataset.testid = 'image-download-link-error';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    // Throw error for non-CORS errors to be handled by call site
    if (!isCorsError) {
      console.error('Image download error:', error);
      throw error;
    }
  };

  const getDownloadFilename = (mimeType?: string): string => {
    const now = DateTime.now();
    const datePart = now.toFormat('yyyy-MM-dd');
    const timePart = now.toFormat('h.mm.ss a');
    const extension: string = mimeType?.toLowerCase() === 'image/png' ? '.png' : '.jpg';
    return t('imageSelect.downloadFilenameFormat', {
      date: datePart,
      time: timePart,
      extension: extension,
    });
  };

  const triggerDownload = (url: string, mimeType?: string, cleanup?: () => void): void => {
    const anchor: HTMLAnchorElement = document.createElement('a');
    anchor.style.display = 'none';
    anchor.href = url;
    anchor.download = getDownloadFilename(mimeType);
    anchor.dataset.testid = 'image-download-link';

    document.body.appendChild(anchor);
    anchor.click();

    setTimeout(() => {
      document.body.removeChild(anchor);
      cleanup?.();
    }, 100);
  };

  const isCrossOrigin = (url: string): boolean => {
    try {
      return new URL(url, window.location.href).origin !== window.location.origin;
    } catch {
      return false;
    }
  };

  const hasCorsOrCrossOriginError = (imageUrl: string, error: unknown): boolean => {
    if (!(error instanceof TypeError)) {
      return false;
    }

    // Check for explicit CORS error messages
    const errorMessage: string = error.message.toLowerCase();
    const corsKeywords: string[] = ['cors', 'access-control', 'access control', 'failed to fetch'];
    const hasCorsKeyword: boolean = corsKeywords.some((keyword) => errorMessage.includes(keyword));

    // Also treat cross-origin TypeErrors as CORS issues
    const isCrossOriginUrl = isCrossOrigin(imageUrl);

    return hasCorsKeyword || isCrossOriginUrl;
  };

  const handleBlobUrlDownload = async (blobUrl: string): Promise<void> => {
    try {
      const response: Response = await fetch(blobUrl);
      const blob: Blob = await response.blob();
      triggerDownload(blobUrl, blob.type);
    } catch {
      // Fallback to JPEG if blob fetch fails
      triggerDownload(blobUrl, 'image/jpeg');
    }
  };

  const handleRemoteUrlDownload = async (imageUrl: string): Promise<void> => {
    try {
      const response: Response = await fetch(imageUrl);

      if (!response.ok) {
        handleDownloadError(imageUrl, response);
        return;
      }

      const blob: Blob = await response.blob();
      const blobUrl: string = URL.createObjectURL(blob);
      triggerDownload(blobUrl, blob.type, () => URL.revokeObjectURL(blobUrl));
    } catch (error) {
      handleDownloadError(imageUrl, error as Error);
    }
  };

  const downloadImage = async (imageUrl: string | null): Promise<void> => {
    if (!imageUrl) return;

    const isBlobUrl: boolean = imageUrl.startsWith('blob:');

    if (isBlobUrl) {
      await handleBlobUrlDownload(imageUrl);
      return;
    }

    await handleRemoteUrlDownload(imageUrl);
  };

  return {
    downloadImage,
  };
};
